import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  getComponentTypeById,
  getComponentTypesLookups,
  getComponentTypesByCollectionComponentId,
  createComponentType,
  getComponentTypesDatatable,
  getComponentTypesByCollectionComponentTypeId,
  getComponentTypeByIdMultilingual,
  createComponentTypeMultilingual,
  editComponentTypeMultilingual,
} from './thunks';
import { ComponentTypesState } from './types';

export const name = 'ComponentTypes';

const initialState: ComponentTypesState = {
  componentTypesDatatable: {
    data: null,
    status: 'idle',
  },
  componentTypesLookups: {
    data: [],
    status: 'idle',
  },
  componentTypesByCollectionComponentId: {
    data: [],
    status: 'idle',
  },
  componentTypesByCollectionComponentTypeId: {
    data: [],
    status: 'idle',
  },
  componentTypeById: {
    data: null,
    status: 'idle',
  },
  createComponentType: {
    data: undefined,
    status: 'idle',
  },
  componentTypeByIdMultilingual: {
    data: null,
    status: 'idle',
  },
  createComponentTypeMultilingual: {
    data: null,
    status: 'idle',
  },
  editComponentTypeMultilingual: {
    data: null,
    status: 'idle',
  },
};

export const getComponentTypesDatatableAsync = createAsyncThunk(
  name + '/ getComponentTypesDatatable',
  getComponentTypesDatatable
);

export const getComponentTypesLookupsAsync = createAsyncThunk(
  name + '/ getComponentTypesLookups',
  getComponentTypesLookups
);

export const getComponentTypeByIdAsync = createAsyncThunk(
  name + '/ getComponentTypeById',
  getComponentTypeById
);

export const getComponentTypesByCollectionComponentIdAsync = createAsyncThunk(
  name + '/ getComponentTypesByCollectionComponentId',
  getComponentTypesByCollectionComponentId
);

export const createComponentTypeAsync = createAsyncThunk(
  name + '/ createComponentType',
  createComponentType
);

export const getComponentTypesByCollectionComponentTypeIdAsync =
  createAsyncThunk(
    name + '/ getComponentTypesByCollectionComponentTypeId',
    getComponentTypesByCollectionComponentTypeId
  );

export const getComponentTypeByIdMultilingualAsync = createAsyncThunk(
  name + '/ getComponentTypeByIdMultilingual',
  getComponentTypeByIdMultilingual
);

export const createComponentTypeMultilingualAsync = createAsyncThunk(
  name + '/ createComponentTypeMultilingual',
  createComponentTypeMultilingual
);

export const editComponentTypeMultilingualAsync = createAsyncThunk(
  name + '/ editComponentTypeMultilingual',
  editComponentTypeMultilingual
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearComponentTypeById: (state) => {
      state.componentTypeById.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getComponentTypesDatatableAsync.pending, (state) => {
        state.componentTypesDatatable.status = 'loading';
      })
      .addCase(getComponentTypesDatatableAsync.fulfilled, (state, action) => {
        state.componentTypesDatatable = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentTypesDatatableAsync.rejected, (state) => {
        state.componentTypesDatatable.status = 'failed';
      })
      .addCase(getComponentTypeByIdAsync.pending, (state) => {
        state.componentTypeById.status = 'loading';
      })
      .addCase(getComponentTypeByIdAsync.fulfilled, (state, action) => {
        state.componentTypeById = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentTypeByIdAsync.rejected, (state) => {
        state.componentTypeById.status = 'failed';
      })
      .addCase(
        getComponentTypesByCollectionComponentIdAsync.pending,
        (state) => {
          state.componentTypesByCollectionComponentId.status = 'loading';
        }
      )
      .addCase(
        getComponentTypesByCollectionComponentIdAsync.fulfilled,
        (state, action) => {
          state.componentTypesByCollectionComponentId = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getComponentTypesByCollectionComponentIdAsync.rejected,
        (state) => {
          state.componentTypesByCollectionComponentId.status = 'failed';
        }
      )
      .addCase(
        getComponentTypesByCollectionComponentTypeIdAsync.pending,
        (state) => {
          state.componentTypesByCollectionComponentTypeId.status = 'loading';
        }
      )
      .addCase(
        getComponentTypesByCollectionComponentTypeIdAsync.fulfilled,
        (state, action) => {
          state.componentTypesByCollectionComponentTypeId = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(
        getComponentTypesByCollectionComponentTypeIdAsync.rejected,
        (state) => {
          state.componentTypesByCollectionComponentTypeId.status = 'failed';
        }
      )
      .addCase(getComponentTypesLookupsAsync.pending, (state) => {
        state.componentTypesLookups.status = 'loading';
      })
      .addCase(getComponentTypesLookupsAsync.fulfilled, (state, action) => {
        state.componentTypesLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getComponentTypesLookupsAsync.rejected, (state) => {
        state.componentTypesLookups.status = 'failed';
      })
      .addCase(createComponentTypeAsync.pending, (state) => {
        state.createComponentType.status = 'loading';
      })
      .addCase(createComponentTypeAsync.fulfilled, (state, action) => {
        state.createComponentType = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(createComponentTypeAsync.rejected, (state) => {
        state.createComponentType.status = 'failed';
      })
      .addCase(getComponentTypeByIdMultilingualAsync.pending, (state) => {
        state.componentTypeByIdMultilingual.status = 'loading';
      })
      .addCase(
        getComponentTypeByIdMultilingualAsync.fulfilled,
        (state, action) => {
          state.componentTypeByIdMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(getComponentTypeByIdMultilingualAsync.rejected, (state) => {
        state.componentTypeByIdMultilingual.status = 'failed';
      })
      .addCase(createComponentTypeMultilingualAsync.pending, (state) => {
        state.createComponentTypeMultilingual.status = 'loading';
      })
      .addCase(
        createComponentTypeMultilingualAsync.fulfilled,
        (state, action) => {
          state.createComponentTypeMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(createComponentTypeMultilingualAsync.rejected, (state) => {
        state.createComponentTypeMultilingual.status = 'failed';
      })
      .addCase(editComponentTypeMultilingualAsync.pending, (state) => {
        state.editComponentTypeMultilingual.status = 'loading';
      })
      .addCase(
        editComponentTypeMultilingualAsync.fulfilled,
        (state, action) => {
          state.editComponentTypeMultilingual = {
            data: action.payload,
            status: 'idle',
          };
        }
      )
      .addCase(editComponentTypeMultilingualAsync.rejected, (state) => {
        state.editComponentTypeMultilingual.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
